import { Fragment, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/DateField";
import { trDates, formatDateTime } from "@/lib/constants";
import { PeriodPresets, RANGE_PRESETS, matchPreset } from "@/components/PeriodPresets";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, FileDown, Printer, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { downloadFile } from "@/lib/download";
import { ACTION_STYLE, ChangesRow } from "@/components/AuditChangesRow";

export const VillageAuditTab = ({ villageId }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
  const qs = new URLSearchParams(params).toString();
  const setR = (k) => (v) => { setPage(1); setRange((r) => ({ ...r, [k]: v })); };
  const applyPreset = (p) => { setPage(1); setRange(p.range()); };
  const activePreset = matchPreset(RANGE_PRESETS, range);

  const pdf = async () => {
    setPdfBusy(true);
    try {
      await downloadFile(`/villages/${villageId}/audit-logs/pdf${qs ? `?${qs}` : ""}`, "koy_islem_gecmisi.pdf");
      toast.success("İşlem geçmişi (PDF) indirildi");
    } catch (e) { toast.error("PDF oluşturulamadı"); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    api.get(`/villages/${villageId}/audit-logs`, { params: { page, page_size: 30, ...params } })
      .then((r) => setData(r.data)).catch((e) => {
        setData({ items: [], total: 0, page_size: 30 });
        toast.error(e?.response?.data?.detail?.toString?.() || "İşlem geçmişi yüklenemedi");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villageId, page, qs]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <>
      <div className="mb-2">
        <PeriodPresets presets={RANGE_PRESETS} active={activePreset} onPick={applyPreset} testPrefix="village-audit" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <DateField value={range.date_from} onChange={setR("date_from")} testId="village-audit-from" />
        <span className="text-slate-400 text-sm">–</span>
        <DateField value={range.date_to} onChange={setR("date_to")} testId="village-audit-to" />
        {qs && <Button variant="ghost" size="sm" onClick={() => { setPage(1); setRange({ date_from: "", date_to: "" }); }} data-testid="village-audit-clear">Temizle</Button>}
        <div className="flex-1" />
        <Button size="sm" onClick={pdf} disabled={pdfBusy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="village-audit-pdf-button">
          {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />} PDF İndir
        </Button>
        <Button size="sm" variant="outline" asChild data-testid="village-audit-print-button">
          <Link to={`/koyler/${villageId}/gecmis-yazdir${qs ? `?${qs}` : ""}`} target="_blank"><Printer className="h-4 w-4 mr-1.5" /> Yazdır</Link>
        </Button>
      </div>
      <Card className="border border-slate-200 shadow-sm overflow-x-auto">
        <Table data-testid="village-audit-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Tarih / Saat</TableHead>
              <TableHead>Kullanıcı</TableHead>
              <TableHead>İşlem</TableHead>
              <TableHead>Kayıt Türü</TableHead>
              <TableHead>Kayıt</TableHead>
              <TableHead className="w-28">Değişiklik</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!data && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Yükleniyor...</TableCell></TableRow>}
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500" data-testid="village-audit-empty">{qs ? "Seçilen tarih aralığında işlem yok." : "Bu köyün kayıtlarında henüz işlem yapılmadı."}</TableCell></TableRow>
            )}
            {data?.items.map((a) => (
              <Fragment key={a.id}>
                <TableRow data-testid={`village-audit-row-${a.id}`}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{formatDateTime(a.at)}</TableCell>
                  <TableCell className="whitespace-nowrap">{a.user_name}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${ACTION_STYLE[a.action] || ""}`}>{a.action}</span></TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{a.entity}</TableCell>
                  <TableCell className="text-sm text-slate-700 min-w-[200px]">{trDates(a.label) || "-"}</TableCell>
                  <TableCell>
                    {a.changes?.length > 0 ? (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-emerald-700"
                              onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))} data-testid={`village-audit-toggle-${a.id}`}>
                        {open[a.id] ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                        {a.changes.length} alan
                      </Button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
                {open[a.id] && a.changes?.length > 0 && <ChangesRow changes={a.changes} />}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm text-slate-600">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="village-audit-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span data-testid="village-audit-page">{page} / {pages}</span>
          <Button variant="outline" size="icon" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="village-audit-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </>
  );
};
